import { OverlayFocusScope } from "@/components/modal/OverlayFocusScope";
import { OverlayProvider } from "@/components/modal/OverlayProvider";
import { OverlayContext, OverlayOwnerContext } from "@/components/modal/overlay-context";
import { useOverlayBackgroundScrollLock } from "@/components/modal/overlay.hooks";
import type { OverlayLayerProps } from "@/components/modal/overlay.types";
import { ZIndex } from "@/constants/z-index";
import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";

// Mount this inside the transition so registration survives until the exit animation finishes.
export const OverlayLayer = React.forwardRef<HTMLDivElement, OverlayLayerProps>(
  function OverlayLayerBoundary(props, forwardedRef) {
    const context = useContext(OverlayContext);
    const layer = <RegisteredOverlay {...props} ref={forwardedRef} />;
    return context ? layer : <OverlayProvider>{layer}</OverlayProvider>;
  },
);

const RegisteredOverlay = React.forwardRef<HTMLDivElement, OverlayLayerProps>(
  function RegisteredOverlay(
    {
      children,
      blocking = true,
      closeOnEscape = true,
      blurEditableOnEscape = false,
      backdropClassName,
      inheritBackdrop = false,
      onClose,
      parentId: explicitParent,
      overlayId,
      lockBackgroundScroll = false,
      closing = false,
    },
    forwardedRef,
  ) {
    const { manager, root, toastRoot } = useContext(OverlayContext)!;
    useOverlayBackgroundScrollLock(lockBackgroundScroll);
    const inheritedParent = useContext(OverlayOwnerContext);
    const parentId = explicitParent === undefined ? inheritedParent : explicitParent;
    const generatedId = useId();
    const id = overlayId ?? generatedId;
    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const entries = useSyncExternalStore(
      manager.subscribe,
      manager.getSnapshot,
      manager.getSnapshot,
    );
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    useLayoutEffect(() => {
      if (!element) return;
      return manager.register({
        id,
        parentId,
        element,
        blocking,
        closeOnEscape,
        closing,
        backdropClassName,
        inheritBackdrop,
        onClose: () => closeRef.current(),
      });
      // Updating options must not change the overlay's opening order.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manager, element, id, parentId]);
    useLayoutEffect(() => {
      manager.update(id, { blocking, closeOnEscape, backdropClassName, inheritBackdrop, closing });
    }, [manager, id, blocking, closeOnEscape, backdropClassName, inheritBackdrop, closing]);
    useLayoutEffect(() => {
      if (closing) manager.dismissDescendants(id);
    }, [manager, id, closing]);
    const index = entries.findIndex((entry) => entry.id === id);
    const activeFocusScope = entries.findLast((entry) => entry.blocking)?.id === id;
    const focusContainers = useMemo(() => {
      const blockingIndex = entries.findLastIndex((entry) => entry.blocking);
      const layers = entries.slice(blockingIndex).map((entry) => entry.element);
      return toastRoot ? [...layers, toastRoot] : layers;
    }, [entries, toastRoot]);
    useEffect(() => {
      if (!element) return;
      const handleEscape = (event: KeyboardEvent) => {
        if (
          event.key !== "Escape" ||
          event.defaultPrevented ||
          event.isComposing ||
          !closeOnEscape ||
          !manager.isEscapeOwner(event, id)
        )
          return;
        event.preventDefault();
        const focused = document.activeElement;
        if (
          blurEditableOnEscape &&
          focused instanceof HTMLElement &&
          element.contains(focused) &&
          (focused.isContentEditable || focused.matches("input, textarea, select"))
        ) {
          focused.blur();
          // Keep focus inside the modal so the trap and the next Escape have a stable target.
          element.focus({ preventScroll: true });
          return;
        }
        closeRef.current();
      };
      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }, [manager, id, element, closeOnEscape, blurEditableOnEscape]);
    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        setElement(node);
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );
    if (!root) return null;
    return createPortal(
      <OverlayOwnerContext.Provider value={id}>
        <OverlayFocusScope
          active={activeFocusScope}
          blocking={blocking}
          containers={focusContainers}
          element={element}
        />
        <div
          ref={setRef}
          onSubmit={(event) => event.stopPropagation()}
          tabIndex={-1}
          data-overlay-id={id}
          data-overlay-layer={index + 1}
          style={{
            position: "fixed",
            inset: 0,
            pointerEvents: "none",
            // Odd slots hold the single dimmer immediately below its blocking owner.
            zIndex: (index + 1) * 2,
          }}
        >
          {children}
          <div
            data-overlay-floating-root
            style={{ position: "relative", zIndex: ZIndex.OVERLAY_ROOT, pointerEvents: "auto" }}
          />
        </div>
      </OverlayOwnerContext.Provider>,
      root,
    );
  },
);
